import { Copy } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "@/components/ui/sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Modal } from "@/components/ui/modal";
import { StatusPill } from "@/components/ui/status-pill";
import { thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";
import {
  ErrorBlock,
  LoadingBlock,
} from "@/features/questionnaire/components/QueryState";

import {
  useDeleteFormationGroup,
  useFormationGroupQuery,
  useGoalCatalogueQuery,
  useRegenerateCode,
  useRemoveParticipant,
  useResetFormationGroup,
  useSetEnrolmentOpen,
} from "../api";
import { nestCatalogue } from "../lib/nest";

/**
 * This app's own enrolment page — `enrolmentRoute` in `app/router.tsx`, which
 * cannot be imported here without a cycle. The Django detail page shows the
 * Django one, as the verification email links back to whichever app the
 * participant started in. The router has no basepath, so the origin and the
 * path are the whole address.
 */
const ENROLMENT_PATH = "/enrolment";

export function FormationGroupDetail({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const group = useFormationGroupQuery(id);
  const catalogue = useGoalCatalogueQuery();
  const removeParticipant = useRemoveParticipant(id);
  const deleteGroup = useDeleteFormationGroup();
  const setEnrolmentOpen = useSetEnrolmentOpen(id);
  const regenerateCode = useRegenerateCode(id);
  const resetGroup = useResetFormationGroup(id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [showingGoals, setShowingGoals] = useState(false);

  const selectedGoalIds = useMemo(
    () => new Set(group.data?.goals ?? []),
    [group.data],
  );

  /** The group's own goals, nested the way the picker presents them. */
  const goalTree = useMemo(() => {
    if (!catalogue.data) return [];
    return nestCatalogue(
      catalogue.data.thematics,
      catalogue.data.issues,
      catalogue.data.goals.filter((goal) => selectedGoalIds.has(goal.id)),
    );
  }, [catalogue.data, selectedGoalIds]);

  // A failed fetch used to render an empty page with no message, where the
  // Django screens fail visibly.
  if (group.error) return <ErrorBlock error={group.error} />;
  if (!group.data) return null;
  const participants = group.data.participants ?? [];
  const enrolmentUrl = `${window.location.origin}${ENROLMENT_PATH}`;

  // The Clipboard API is missing outside a secure context, so the address is
  // selected and copied the old way too, as the Django page does. Left
  // selected on failure, so it can still be copied by hand.
  const copyEnrolmentLink = async () => {
    try {
      await navigator.clipboard.writeText(enrolmentUrl);
      toast.success(t("formationGroups.enrolment.linkCopied"));
      return;
    } catch {
      // Fall through to the selection fallback.
    }
    const address = document.getElementById("enrolment-link");
    const selection = window.getSelection();
    let copied = false;
    if (address && selection) {
      const range = document.createRange();
      range.selectNodeContents(address);
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }
    }
    if (copied) toast.success(t("formationGroups.enrolment.linkCopied"));
    else toast.error(t("formationGroups.enrolment.linkCopyFailed"));
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Takes what the actions leave, so the enrolment link line wraps
            inside it instead of pushing the actions onto a row of their own. */}
        <div className="min-w-0 flex-[1_1_20rem]">
          <h1 className="text-2xl font-semibold text-foreground">
            {group.data.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("formationGroups.detail.participantCount", {
              count: participants.length,
            })}{" "}
            —{" "}
            <button
              type="button"
              onClick={() => setShowingGoals(true)}
              className="text-primary underline underline-offset-2 hover:text-primary/80"
            >
              {t("formationGroups.detail.goalCount", {
                count: group.data.goals?.length ?? 0,
              })}
            </button>{" "}
            — {t("formationGroups.detail.createdOn")}{" "}
            {formatDate(group.data.created_at)}
          </p>
          {/* Where the code is entered, as a fact about the group under its
              name, so it can be shared from here. Plain text rather than a
              field: one click selects all of it (`select-all`), which is also
              what the copy fallback selects. The Django header carries it in the
              same place. */}
          <p className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-sm">
            <span className="text-muted-foreground">
              {t("formationGroups.enrolment.link")}
            </span>
            <span
              id="enrolment-link"
              className="select-all break-all font-mono text-[13px] text-foreground"
            >
              {enrolmentUrl}
            </span>
            <Button
              size="sm"
              variant="link"
              className="h-auto p-0 font-semibold"
              onClick={copyEnrolmentLink}
            >
              <Copy className="size-3.5" aria-hidden />
              {t("formationGroups.enrolment.copyLink")}
            </Button>
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/formation-groups/$id/edit" params={{ id }}>
              {t("formationGroups.edit")}
            </Link>
          </Button>
          {/* Neutral, while Supprimer beside it keeps the destructive
              variant: the ticket asks for the two not to be confused, and they
              read as different kinds of act rather than two intensities of one.
              The reset is the recoverable one — the group survives with its
              name and its goals, every account survives, and the code is
              reissued rather than lost — so the weight goes on the
              confirmation's commit button instead. The Django header carries
              the same relationship. */}
          <Button variant="outline" onClick={() => setConfirmingReset(true)}>
            {t("formationGroups.reset")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => setConfirmingDelete(true)}
          >
            {t("formationGroups.delete")}
          </Button>
        </div>
      </div>

      {/* No card title: the code is the subject, so announcing it above
          spent a whole row on the word "Inscriptions" and pushed the state
          pill away from the thing it describes. The pill sits beside the code
          now and the actions close the row. The label survives for assistive
          tech, where six unannounced characters read as noise. */}
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <p className="text-2xl">
              <span className="sr-only">
                {t("formationGroups.enrolment.code")}{" "}
              </span>
              <span className="font-mono font-semibold tracking-widest text-foreground">
                {group.data.code}
              </span>
            </p>
            <StatusPill tone={group.data.is_open ? "success" : "muted"}>
              {group.data.is_open
                ? t("formationGroups.enrolment.open")
                : t("formationGroups.enrolment.closed")}
            </StatusPill>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={group.data.is_open ? "outline" : "default"}
                disabled={setEnrolmentOpen.isPending}
                onClick={() =>
                  setEnrolmentOpen.mutate(!group.data.is_open, {
                    onSuccess: () =>
                      toast.success(
                        group.data.is_open
                          ? t("formationGroups.toasts.closed", {
                              name: group.data.name,
                            })
                          : t("formationGroups.toasts.opened", {
                              name: group.data.name,
                              code: group.data.code,
                            }),
                      ),
                    onError: () => toast.error(t("formationGroups.error")),
                  })
                }
              >
                {group.data.is_open
                  ? t("formationGroups.enrolment.close")
                  : t("formationGroups.enrolment.openAction")}
              </Button>
              {/* Confirmed, because the old code stops working the moment this
                  fires and a room that has already been given it is left
                  outside. */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmingRegenerate(true)}
              >
                {t("formationGroups.enrolment.regenerate")}
              </Button>
            </div>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {group.data.is_open
              ? t("formationGroups.enrolment.usableHint")
              : t("formationGroups.enrolment.closedHint")}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("formationGroups.detail.participants")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  {/* Three columns, not five: the name, the email and the
                      journey step describe one person, so they share a cell.
                      The Django table was reshaped the same way; there it was
                      also what gave that app's five destination links the
                      width they needed. This column holds two, because
                      `ParticipantDataView` folds the assessment, results,
                      action plan and history into one screen. */}
                  <th className="pb-2 pr-4 font-medium">
                    {t("formationGroups.detail.participant")}
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    {t("formationGroups.detail.data")}
                  </th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {participants.map((participant) => (
                  <tr
                    key={participant.membership_id}
                    className="border-b border-border align-top"
                  >
                    <td className="py-3 pr-4">
                      <span className="flex flex-col gap-0.5">
                        <span className="font-medium text-foreground">
                          {participant.name}
                        </span>
                        {/* The name the participant typed, not the fabricated
                            one behind it — `participant_rows` reads
                            `display_name`. Empty until the enterprise profile
                            exists, so it is gated rather than rendered blank,
                            exactly as the Django row gates it. */}
                        {participant.enterprise_name ? (
                          <span className="text-sm text-foreground">
                            {participant.enterprise_name}
                          </span>
                        ) : null}
                        <span className="text-xs text-muted-foreground">
                          {participant.email}
                        </span>
                        <span className="mt-1.5">
                          {/* The shared back-office status pill, the same one
                              the financial-aid and tool lists use and the
                              counterpart of Django's `.status-chip`. A
                              participant's step is a status, so it gets that
                              shape rather than a private one — and the tone
                              tokens carry the accessible text colour with
                              them. */}
                          <StatusPill
                            tone={
                              participant.enterprise_profile_id
                                ? "info"
                                : "muted"
                            }
                          >
                            {participant.step_label}
                          </StatusPill>
                        </span>
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      {/*
                        Gated on the profile existing and on nothing else — an
                        administrator is not subject to consent, and a
                        participant who has not created a profile yet has no id
                        for any of these destinations.
                      */}
                      {participant.enterprise_profile_id ? (
                        <div className="flex flex-wrap gap-3">
                          <Link
                            className="text-primary underline-offset-4 hover:underline"
                            to="/formation-groups/$id/participants/$profileId"
                            params={{
                              id,
                              profileId: participant.enterprise_profile_id,
                            }}
                          >
                            {t("formationGroups.detail.assessment")}
                          </Link>
                          <Link
                            className="text-primary underline-offset-4 hover:underline"
                            to="/formation-groups/$id/participants/$profileId/profile"
                            params={{
                              id,
                              profileId: participant.enterprise_profile_id,
                            }}
                          >
                            {t("formationGroups.detail.profile")}
                          </Link>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          {t("formationGroups.detail.noProfile")}
                        </span>
                      )}
                    </td>
                    <td className="py-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (!window.confirm(t("formationGroups.detail.removeConfirm")))
                            return;
                          removeParticipant.mutate(participant.membership_id, {
                            onSuccess: () =>
                              toast.success(
                                t("formationGroups.toasts.participantRemoved", {
                                  participant: participant.name,
                                }),
                              ),
                            onError: () =>
                              toast.error(t("formationGroups.error")),
                          });
                        }}
                      >
                        {t("formationGroups.detail.remove")}
                      </Button>
                    </td>
                  </tr>
                ))}
                {participants.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-muted-foreground">
                      {t("formationGroups.detail.noParticipants")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* The goal set is what the group is for, but it is read once when the
          cohort is set up and then never again — so the page states its size
          and keeps the list a click away, rather than letting 99 bullet points
          push the participants off the screen. The Django detail page was
          reshaped the same way. */}
      {showingGoals ? (
        <Modal
          onClose={() => setShowingGoals(false)}
          title={t("formationGroups.detail.goals")}
        >
          {/* The count on the trigger comes from the group, the list from the
              catalogue — two queries. Without these two states an in-flight or
              failed catalogue renders "no goal selected" underneath a trigger
              that correctly says there are three, which is a wrong answer told
              with the same confidence as a right one. Django cannot reach this:
              both numbers come off one queryset in one request. */}
          <div className="max-h-[60vh] space-y-5 overflow-y-auto">
            {catalogue.error ? (
              <ErrorBlock error={catalogue.error} />
            ) : !catalogue.data ? (
              <LoadingBlock />
            ) : goalTree.length ? (
              goalTree.map(({ thematic, issues }) => {
                const theme = thematicClasses(thematic.css_class);
                return (
                  <section key={thematic.id}>
                    <h3 className="flex items-center gap-2 font-semibold text-foreground">
                      <span
                        aria-hidden
                        className={cn("size-2 shrink-0 rounded-full", theme.solid)}
                      />
                      {thematic.name}
                    </h3>
                    {issues.map(({ issue, goals }) => (
                      <div key={issue.id} className="mt-2">
                        <p className="text-sm font-semibold text-muted-foreground">
                          {issue.name}
                        </p>
                        <ul className="ml-4 list-disc text-sm text-foreground">
                          {goals.map((goal) => (
                            <li key={goal.id}>{goal.name}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </section>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("formationGroups.detail.noGoals")}
              </p>
            )}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowingGoals(false)}>
              {t("formationGroups.detail.close")}
            </Button>
            <Button asChild>
              <Link to="/formation-groups/$id/edit" params={{ id }}>
                {t("formationGroups.detail.editGoals")}
              </Link>
            </Button>
          </div>
        </Modal>
      ) : null}

      {confirmingRegenerate ? (
        <Modal
          onClose={() => setConfirmingRegenerate(false)}
          title={t("formationGroups.regenerateDialog.title")}
        >
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              {t("formationGroups.regenerateDialog.body", {
                code: group.data.code,
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("formationGroups.regenerateDialog.participants")}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmingRegenerate(false)}
              >
                {t("formationGroups.cancel")}
              </Button>
              <Button
                disabled={regenerateCode.isPending}
                onClick={() =>
                  regenerateCode.mutate(undefined, {
                    // The new code comes off the mutation's own response: the
                    // detail query has not refetched yet when this fires, so
                    // `group.data.code` here would still be the retired one —
                    // the single most misleading thing this toast could say.
                    onSuccess: (data) => {
                      toast.success(
                        t("formationGroups.toasts.regenerated", {
                          name: group.data.name,
                          code: data.code,
                        }),
                      );
                      setConfirmingRegenerate(false);
                    },
                    onError: () => toast.error(t("formationGroups.error")),
                  })
                }
              >
                {t("formationGroups.enrolment.regenerate")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* The reset confirmation. Unlike the single-participant removal, which
          is a bare `window.confirm`, this one carries a count and says what
          survives — and the Django side is a modal, so a `confirm` here would
          be a divergence on the one surface the two apps are compared on. */}
      {confirmingReset ? (
        <Modal
          onClose={() => setConfirmingReset(false)}
          title={t("formationGroups.resetDialog.title")}
        >
          <div className="space-y-4">
            {/* i18next runs JSON v4 here, which selects `_zero` at count === 0
                whatever the language's plural rule — so the empty group gets
                its own sentence with no branch in this component. French
                would otherwise pluralise zero as the singular and promise
                that one participant is about to be removed. */}
            <p className="text-sm text-foreground">
              {t("formationGroups.resetDialog.body", {
                count: participants.length,
                name: group.data.name,
              })}
            </p>
            {/* Shown in every state, the empty one included: a reset always
                retires the code, so there is no state in which this does not
                belong. Word for word the regenerate dialog's sentence — it is
                the same fact, and wording it twice is how the two screens
                start disagreeing. */}
            <p className="text-sm text-foreground">
              {t("formationGroups.regenerateDialog.body", {
                code: group.data.code,
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("formationGroups.resetDialog.preserved")}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmingReset(false)}
              >
                {t("formationGroups.cancel")}
              </Button>
              <Button
                variant="destructive"
                disabled={resetGroup.isPending}
                onClick={() =>
                  resetGroup.mutate(undefined, {
                    // Both numbers come off the mutation's own response. The
                    // detail query has not refetched when this fires, so
                    // `group.data.code` would still be the retired one, and
                    // `participants.length` is whatever this page last saw —
                    // which a concurrent removal has already made wrong.
                    onSuccess: (data) => {
                      setConfirmingReset(false);
                      toast.success(
                        t("formationGroups.toasts.reset", {
                          count: data.removed_participants,
                          name: group.data.name,
                          code: data.code,
                        }),
                      );
                    },
                    onError: () => toast.error(t("formationGroups.error")),
                  })
                }
              >
                {t("formationGroups.resetDialog.confirm")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {confirmingDelete ? (
        <Modal
          onClose={() => setConfirmingDelete(false)}
          title={t("formationGroups.deleteDialog.title")}
        >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            {t("formationGroups.deleteDialog.body", { name: group.data.name })}
          </p>
          {/* French pluralises zero as the singular, so the sentence would
              promise an untouched account where the group holds none. */}
          {participants.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("formationGroups.deleteDialog.accounts", {
                count: participants.length,
              })}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmingDelete(false)}
            >
              {t("formationGroups.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deleteGroup.mutate(id, {
                  onSuccess: () => {
                    toast.success(
                      t("formationGroups.toasts.deleted", {
                        name: group.data.name,
                      }),
                    );
                    navigate({ to: "/formation-groups" });
                  },
                  onError: () => toast.error(t("formationGroups.error")),
                })
              }
            >
              {t("formationGroups.delete")}
            </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
